import React, { useCallback, useMemo, useRef, useState } from "react";

import { isEqual } from "lodash";
import useSWR from "swr";
import "./GlobalItemList.css";
import viewIcon from "../../Assets/Images/eye.png";
import useAuth from "../../ContextProvider/AuthProvider";
import { addItemtoObject, hexToRgba, jsonToQueryString } from "../../Utils.js";
import PawItemList from "../Common/PawItemList.js";
import { Button, Modal, Box, Typography, Grid, IconButton, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../Api/ApiServices.js";
import Layout from "../Side bar/Layout.js";
import { MainPageWrapper } from "./GlobalItemList";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

export default function History() {
  const { userId, user } = useAuth();
  const theme = useTheme();

  const [filter, setFilter] = useState({
    limit: 15,
    from: 0,
    page: 1,
    sortBy: "",
    order: "",
  });
  const [pageCount, setPageCount] = useState(0);
  const [selectedData, setSelectedData] = useState("{}");
  const [updatedValue, setUpdatedValue] = useState();
  const fetchIdRef = useRef(0);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { t } = useTranslation();

  const {
    data = [],
    mutate: fetchEmergencyData,
    isLoading,
  } = useSWR(
    `Historydata${userId}-${jsonToQueryString(filter)}`,
    async () => {
      const { page, limit, sortBy, order } = filter;
      const { data } = await ApiService.getHistoryByUserId(userId, page, sortBy, order);
      if (data?.length && limit) {
        setPageCount(Math.ceil(data?.[0]?.totalRows / limit));
      }
      return data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (oFilter.sort) {
      const isDesc = oFilter.sort.startsWith("-");
      oFilter.sortBy = isDesc ? oFilter.sort.substring(1) : oFilter.sort;
      oFilter.order = isDesc ? "desc" : "asc";
    } else {
      oFilter.sortBy = "";
      oFilter.order = "";
    }
    // if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
    setFilter(oFilter);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleView = (item, type) => {
    const propertiesToDelete = ["id"];
    const formattedValue = addItemtoObject(item, propertiesToDelete);
    setUpdatedValue(formattedValue?.data);
    const selectedValues =
      type === "new_value" ? item?.new_value : item?.old_value;
    setSelectedData(selectedValues);
    handleOpen();
  };

  const columns = useMemo(() => {
    return [
      {
        Header: `${t("screens.history.formName")}`,
        textAlign: "center",
        accessor: "table_name",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.history.operation")}`,
        textAlign: "center",
        accessor: "operations",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.history.newValue")}`,
        accessor: "new_value",
        textAlign: "center",
        width: "12.5vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          if (!row.original.new_value) {
            return "N/A";
          }
          return (
            <>
              <Button
                color="info"
                sx={{
                  minWidth: "40px",
                  background: `${hexToRgba(theme.palette.info.main, 0.10)} !important`,
                  borderRadius: "10px",
                  marginRight: "5px",
                  width: "24px",
                }}
                onClick={() => handleView(row.original, "new_value")}
              >
                <img
                  alt="view"
                  src={viewIcon}
                  style={{
                    color: "#000000",
                    cursor: "pointer",
                  }}
                />
              </Button>
            </>
          );
        },
      },
      {
        Header: `${t("screens.history.oldValue")}`,
        accessor: "old_value",
        textAlign: "center",
        width: "12.5vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          if (!row.original.old_value) {
            return "N/A";
          }
          return (
            <>
              <Button
                color="info"
                sx={{
                  minWidth: "40px",
                  background: `${hexToRgba(theme.palette.info.main, 0.10)} !important`,
                  borderRadius: "10px",
                  marginRight: "5px",
                  width: "24px",
                }}
                onClick={() => handleView(row.original, "old_value")}
              >
                <img
                  alt="view"
                  src={viewIcon}
                  style={{
                    color: "#000000",
                    cursor: "pointer",
                  }}
                />
              </Button>
            </>
          );
        },
      },
      {
        Header: `${t("screens.history.modifiedDate")}`,
        accessor: "modified",
        textAlign: "center",
        width: "12.5vw",
        Cell: ({ row }) => {
          // const date = new Date(Number(row?.original?.created));
          const date = row?.original?.modified
          const formattedDate = formatModifiedDate(date)
          // const formattedDate = `${
          //   date.getMonth() + 1
          // }/${date.getDate()}/${date.getFullYear()} ${String(
          //   date.getHours()
          // ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
          return formattedDate;
        },
      },
      {
        Header: `${t("screens.history.modifiedBy")}`,
        accessor: "user_fname",
        textAlign: "center",
        width: "12.5vw",
        Cell: ({ row }) => {
          return `${row.original.user_fname} ${row.original.user_lname}`;
        },
      },
    ];
  }, [t, theme.palette.info.main]);

  const defaultOptions = {
    pageSize: 15,
  };

  const props = {
    label: "",
    isLoading: isLoading,
    columns,
    columnsData: data,
    details: " ",
    fetchData,
    pageCount,
    defaultOptions,
    addButton: false,
    pagination: false,
  };

  const listData = useMemo(() => {
    const parsedValue = JSON.parse(selectedData || "{}");
    delete parsedValue?.created;
    delete parsedValue?.user_id;
    delete parsedValue?.modified_by;
    delete parsedValue?.id;
    if (parsedValue?.physiciantype_value) {
      parsedValue['physiciantype'] = parsedValue?.physiciantype_value;
      delete parsedValue?.physiciantype_value;
    } else if (parsedValue?.physiciantype) {
      parsedValue['physiciantype'] = "";
      delete parsedValue?.physiciantype_value;
    }
    if (parsedValue?.primary_physician) {
      parsedValue['primary_physician'] = "Yes";
    } else if (parsedValue?.primary_physician === "") {
      parsedValue['primary_physician'] = "No";
    }
    if (parsedValue?.ivc_filter) {
      parsedValue['ivc_filter'] = 'Yes';
    } else if (parsedValue?.ivc_filter === '') {
      parsedValue['ivc_filter'] = 'No';
    }
    if (parsedValue?.sergery_impan) {
      parsedValue['surgery_impan'] = 'Yes';
    } else if (parsedValue?.sergery_impan === '') {
      parsedValue['surgery_impan'] = 'No';
    }
    delete parsedValue?.sergery_impan;
    if (parsedValue?.sergery_chronic) {
      parsedValue['surgery_chronic'] = parsedValue?.sergery_chronic;
      delete parsedValue?.sergery_chronic;
    }
    if (parsedValue?.sergery_hxhit) {
      parsedValue['surgery_hxhit'] = parsedValue?.sergery_hxhit;
      delete parsedValue?.sergery_hxhit;
    }
    console.log('parsedValue: ', parsedValue);
    return parsedValue;
  }, [selectedData]);

  const capitalizeAndReplaceUnderscore = (str) => {
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const formatDate = (date) => {
    if (!date) return "-";
    const formattedDate = new Date(date);
    const year = formattedDate.getFullYear();
    const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
    const day = String(formattedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formatModifiedDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    return date?.toISOString()?.split("T")[0]?.replace(/-/g, "/") || "";
  };

  const dateKey = ["date_of_birth", "modified", "created", "pres_startdate"];
  return (
    <Layout>
      <MainPageWrapper>
        <PawItemList {...props} />
        <Modal
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <Box sx={{
              p: 1.5,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: "var(--radium-info-600)",
              color: "#FFFFFF",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
            }}>
              <Typography id="modal-modal-title" variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                {t("screens.history.details")}
              </Typography>
              <IconButton onClick={handleClose} size="small" sx={{ color: "#FFFFFF", p: 0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{
              overflowY: "auto",
              maxHeight: '65vh',
              p: 2,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '10px',
              }
            }}>
              <Grid container spacing={0.5}>
                {Object.entries(listData)?.map(([key, value], index) => {
                  const updated = updatedValue[key];
                  const isUpdated =
                    updated && (updated.oldValue !== value || updated.newValue);
                  return (
                    <React.Fragment key={key}>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 1.25,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          background: index % 2 === 0 ? "transparent" : "color-mix(in srgb, var(--radium-info-600), transparent 97%)",
                          transition: "background 0.2s",
                          '&:hover': {
                            background: "color-mix(in srgb, var(--radium-info-600), transparent 92%)"
                          }
                        }}>
                          <Typography sx={{
                            color: "#737373",
                            fontSize: "13px",
                            fontWeight: 500,
                            width: "35%",
                            flexShrink: 0
                          }}>
                            {capitalizeAndReplaceUnderscore(key)}
                          </Typography>
                          <Typography sx={{
                            color: isUpdated ? "var(--radium-info-600)" : "#313131",
                            fontSize: "13px",
                            fontWeight: isUpdated ? 600 : 400,
                            wordBreak: 'break-word',
                            pl: 2
                          }}>
                            {dateKey.includes(key) ? formatDate(value) : (value || "-")}
                          </Typography>
                        </Box>
                        {index < Object.entries(listData).length - 1 && (
                          <Divider sx={{ opacity: 0.2, mx: 1 }} />
                        )}
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        </Modal>
      </MainPageWrapper>
    </Layout>
  );
}

const modalStyle = {
  position: "relative",
  width: { xs: "90%", sm: 400 },
  bgcolor: "background.paper",
  boxShadow: "0 12px 32px -10px rgba(0, 0, 0, 0.1)",
  borderRadius: 2,
  overflow: 'hidden',
  border: "1px solid color-mix(in srgb, var(--radium-info-600), transparent 80%)",
};
